// ============================================================================
// roles.js — CATÁLOGO DE PAPÉIS DE USUÁRIO (fonte única)
// ----------------------------------------------------------------------------
// `id`    = operacional.usuarios.id_tipo_usuario (int gravado no banco/JWT)
// `key`   = chave usada no código (permissions.<key>) e nas rotas legadas
// `nome`  = rótulo exibido na tela de administração de papéis e em /usuarios
// `ativo` = false quando o papel foi inativado no banco (tipos_usuario.active='N').
//           A entrada FICA aqui de propósito: o mapa id -> chave ainda precisa
//           existir caso o papel seja reativado pela tela de papéis.
// `setor` = recorte da lista de relatórios (tipos_usuario.setor_relatorio).
//           0 = todos · 3 = avaria. NÃO é permissão — é fatia de dado.
//           Fallback usado quando a API de permissões não responde.
// `exigeEntidade` = papel que só existe amarrado a uma entidade (cliente do
//           portal). Fica fora do select de /usuarios, que não coleta
//           id_entidade e criaria usuário quebrado.
// `sistemas` = onde o papel é usado. Documental: OPE = este sistema,
//           AGEND = portal de agendamento, APP = aplicativo Flutter.
//
// ATENÇÃO — `operacional.usuarios` é COMPARTILHADA por 3 sistemas, todos com o
// mesmo SECRET_KEY e o mesmo payload de JWT:
//   OPE    front operacional + operacional-api-nodejs + operacional-api-python
//   AGEND  portal de agendamento + agendamento-api-python (services/auth.py)
//   APP    avist_ttw_flutter + api própria (APP/stitch_login_avist_ttw)
// A lista abaixo é o catálogo COMPLETO, incluindo papéis que não têm tela
// nenhuma no OPE. Eles precisam existir aqui para o mapa id -> chave estar certo
// e para a tela de papéis poder configurá-los. A fonte cruzada mais completa é
// APP/stitch_login_avist_ttw/avist_ttw_flutter/lib/utils/perfil_usuario.dart.
//
// Adicionar um papel novo: pela tela /manutencao/papeis (cria no banco, não
// precisa mexer aqui). Este arquivo é o fallback offline e a origem do seed
// em ddl_rbac.sql da operacional-api-nodejs.
//
// REGRA: nunca renomeie uma `key` existente — ela é o vínculo com o banco e
// com as ~40 rotas que ainda checam requiredPermissions.
// ============================================================================

export const ROLES = [
  { id: 1616, key: 'dev', nome: 'Desenvolvedor' },
  { id: 125, key: 'admin', nome: 'Administrador' },
  { id: 150, key: 'gGeral', nome: 'Gerência Geral' },
  { id: 550, key: 'gerencPatio', nome: 'Gerência de Pátio' },
  { id: 350, key: 'supDoc', nome: 'Supervisor Documental' },
  { id: 320, key: 'documental', nome: 'Documental' },
  { id: 340, key: 'agendamento', nome: 'Agendamento' },
  { id: 300, key: 'gate', nome: 'Gate' },
  { id: 450, key: 'supAva', nome: 'Supervisor Avaria', setor: 3 },
  { id: 400, key: 'avaria', nome: 'Avaria' },
  { id: 330, key: 'financeiro', nome: 'Financeiro' },
  // Perfis do aplicativo (APP). Nenhuma rota do OPE aceita esses papéis hoje.
  { id: 500, key: 'vistoria', nome: 'Vistoria', sistemas: ['APP'] },
  { id: 520, key: 'conferente', nome: 'Conferente', sistemas: ['APP'] },
  { id: 540, key: 'oficina', nome: 'Oficina', sistemas: ['APP'] },
  // Revisor da vistoria de acompanhamento externo. No OPE só alcança
  // /seabox_login e /seabox, que são rotas públicas (login próprio).
  { id: 600, key: 'seabox', nome: 'Seabox (revisor externo)', sistemas: ['APP'] },
  // Cliente do Portal de Agendamento. Criado por agendamento-api-python
  // (services/users_management.py) sempre com id_entidade.
  { id: 200, key: 'cliente', nome: 'Cliente (Portal de Agendamento)', sistemas: ['AGEND'], exigeEntidade: true },
  // Master da empresa no Portal de Agendamento (agendamento-api-python
  // services/users_management.py:post_master_user). Maior grupo da base
  // (1.631 ativos) e não tinha entrada aqui — RBAC_BACKEND_AUDITORIA.md §4/§6.6.
  { id: 250, key: 'clienteSup', nome: 'Cliente Master (Portal de Agendamento)', sistemas: ['AGEND'], exigeEntidade: true },
];

// id_tipo_usuario -> key
export const ROLE_BY_ID = ROLES.reduce((acc, r) => {
  acc[r.id] = r.key;
  return acc;
}, {});

// key -> role
export const ROLE_BY_KEY = ROLES.reduce((acc, r) => {
  acc[r.key] = r;
  return acc;
}, {});

export const ROLE_KEYS = ROLES.map((r) => r.key);

/** Só os papéis atribuíveis (exclui os inativados). */
export const ROLES_ATIVOS = ROLES.filter((r) => r.ativo !== false);

/**
 * Papéis que a tela /usuarios do Operacional pode atribuir. Exclui o cliente do
 * portal, que precisa de id_entidade — campo que aquele formulário não tem.
 */
export const ROLES_ATRIBUIVEIS = ROLES_ATIVOS.filter((r) => !r.exigeEntidade);

export default ROLES;
